import autobind from 'core-decorators/lib/autobind';
import { intlShape, FormattedMessage, FormattedNumber, FormattedDate } from 'react-intl';

import { WixConnect } from 'widget/player-widget/utils/wix-connect';
import { isExternalVideo } from 'shared/ui-selectors/video/video-origin';
import { getMinPriceInfo, getRentPriceInfo, getSalePriceInfo, getSubscriptionPriceInfo } from 'shared/utils/deal-info-helpers';

import { layoutStyleValues } from 'default-settings';

import { OVERLAY_ACTION_TYPES } from './overlay-action-utils';
import { pickContextTypes } from '../context-types';

import ActionButton from '../action-button/action-button';
import InfoOverlayInfo from '../info-overlay/info/info';
import InfoOverlayShare from '../info-overlay/share/share';

import styles from './overlay-actions.styl';

import translations from './translations';


@WixConnect(styles)
export default class OverlayActions extends React.Component {

  static contextTypes = pickContextTypes(
    'toggleInfoOverlay',
    'togglePause',
    'exitFullScreen',
    'videoItem',
    'channelData',
    'appSettings',
    'simple',
    'mobileMode',
    'beenPlayed',
    'ended'
  );

  static propTypes = {
    intl: intlShape,
    appSettings: React.PropTypes.object,
    onPurchaseClick: React.PropTypes.func,
    onSubscribeClick: React.PropTypes.func,
    actionsList: React.PropTypes.arrayOf(React.PropTypes.string),
    onActionClicked: React.PropTypes.func,
  };

  static defaultProps = {
    onActionClicked: _.noop,
  };

  @autobind
  onPurchaseClickHandler() {
    const { onPurchaseClick } = this.props;
    const { togglePause, exitFullScreen } = this.context;

    togglePause(true);
    exitFullScreen();
    setTimeout(onPurchaseClick, 0);
  }

  @autobind
  onSubscribeClickHandler() {
    const { onSubscribeClick } = this.props;
    const { togglePause, exitFullScreen } = this.context;

    togglePause(true);
    exitFullScreen();
    setTimeout(onSubscribeClick, 0);
  }

  getTimer(actionName) {
    const rentInfo = _.get(this.context.videoItem.dgsInfo, 'rent');
    const date = (
      <FormattedDate value={rentInfo.expirationTs * 1000}
                     month="short"
                     day="numeric" />
    );

    return {
      icon: 'timer',
      actionName,
      label: (
        <FormattedMessage {...translations.rented}
                          values={{ date }} />
      ),
    };
  }

  getFilm(actionName) {
    return {
      icon: 'film',
      actionName,
      label: (
        <FormattedMessage {...translations.purchased} />
      ),
    };
  }

  getSubscribe(actionName) {
    const dealInfo = getSubscriptionPriceInfo(this.context.channelData.dealInfo);

    const price = (
      <FormattedNumber value={dealInfo.price}
                       style="currency"
                       currency={dealInfo.currency} />
    );
    return {
      icon: 'buy-rent',
      className: 'qa-widget-overlay-buy-rent-button',
      actionName,
      label: (
        <FormattedMessage {...translations.subscribe}
                          values={{ price }} />
      ),
      onClick: this.onSubscribeClickHandler,
    };
  }

  getBuyRent(actionName) {
    const { dealInfo } = this.context.videoItem;

    const minVideoPriceInfo = getMinPriceInfo(dealInfo);
    const saleDealInfo = getSalePriceInfo(dealInfo);
    const rentDealInfo = getRentPriceInfo(dealInfo);

    let rentSaleDealInfo;
    let message;

    if (saleDealInfo && rentDealInfo) {
      rentSaleDealInfo = minVideoPriceInfo;
      message = translations.buyFrom;
    } else if (saleDealInfo) {
      rentSaleDealInfo = saleDealInfo;
      message = translations.buy;
    } else if (rentDealInfo) {
      rentSaleDealInfo = rentDealInfo;
      message = translations.rent;
    }

    if (rentSaleDealInfo && message) {
      const values = {
        price: <FormattedNumber value={rentSaleDealInfo.price}
                                style="currency"
                                currency={rentSaleDealInfo.currency} />,
      };

      return {
        icon: 'buy-rent',
        className: 'qa-widget-overlay-buy-rent-button',
        actionName,
        label: (
          <FormattedMessage {...message}
                            values={values} />
        ),
        onClick: this.onPurchaseClickHandler,
      };
    }
    return null;
  }

  getVideoInfo(actionName) {
    const { toggleInfoOverlay, videoItem } = this.context;

    if (isExternalVideo(videoItem)) {
      return null;
    }

    return {
      icon: 'info',
      className: 'qa-widget-overlay-info-button',
      actionName,
      onClick() {
        toggleInfoOverlay(<InfoOverlayInfo />, {
          noPoster: true,
        });
      },
    };
  }

  getVideoShare(actionName) {
    const { toggleInfoOverlay } = this.context;
    return {
      icon: 'share',
      className: 'qa-widget-overlay-share-button',
      actionName,
      onClick() {
        toggleInfoOverlay(<InfoOverlayShare />, {
          closeOnBodyClick: true,
          isFullWidth: true,
        });
      },
    };
  }

  get actions() {
    return _(this.props.actionsList)
      .map(actionName => {
        switch (actionName) {
          case OVERLAY_ACTION_TYPES.TIMER:
            return this.getTimer(actionName);

          case OVERLAY_ACTION_TYPES.FILM:
            return this.getFilm(actionName);

          case OVERLAY_ACTION_TYPES.SUBSCRIBE:
            return this.getSubscribe(actionName);

          case OVERLAY_ACTION_TYPES.BUY_RENT:
            return this.getBuyRent(actionName);

          case OVERLAY_ACTION_TYPES.INFO:
            return this.getVideoInfo(actionName);

          case OVERLAY_ACTION_TYPES.SHARE:
            return this.getVideoShare(actionName);

          default:
            return null;
        }
      })
      .compact()
      .map(this.addRounded)
      .map(this.addClickCallback)
      .value();
  }

  @autobind
  addRounded(viewAction) {
    const { appSettings } = this.props;
    const rounded = appSettings.numbers.layoutStyle === layoutStyleValues.ROUND;
    viewAction.rounded = rounded;
    return viewAction;
  }

  @autobind
  addClickCallback(viewAction) {
    if (viewAction.onClick && viewAction.action) {
      const target = viewAction.onClick;
      const { actionName } = viewAction;
      viewAction.onClick = (...args) => {
        target(...args);
        this.props.onActionClicked(actionName);
      };
    }
    return viewAction;
  }

  render() {
    const { simple, mobileMode, beenPlayed, ended } = this.context;

    if (simple || mobileMode) {
      return null;
    }

    if (!beenPlayed || ended) {
      return null;
    }

    return (
      <div styleName="actions"
           className="qa-player-overlay-actions">
        {
          _.map(this.actions, action => (
            <ActionButton styleName="action-button"
                          key={action.icon}
                          {...action} />
          ))
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/player/overlay-actions/overlay-actions.js